<template>
  <div
    class="avatar-panel"
    :class="{
      'full-width': fullWidth,
      'agent-speaking': isAgentSpeaking,
      'agent-thinking': isAgentThinking,
    }"
  >
    <div class="avatar-container">
      <ControlledGif
        :src="avatarImage"
        :playing="isAgentSpeaking"
        alt="Avatar"
        class="avatar-gif"
      />
      <div
        v-if="isAgentThinking && !isAgentSpeaking"
        class="thinking-orbit"
        aria-hidden="true"
      >
        <span></span>
        <span></span>
        <span></span>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import ControlledGif from './ControlledGif.vue';

/**
 * 头像面板组件
 * 负责显示智能体头像
 */
export default defineComponent({
  name: 'AvatarPanel',
  components: {
    ControlledGif,
  },
  props: {
    /**
     * 是否使用全宽模式
     */
    fullWidth: {
      type: Boolean,
      default: false,
    },
    /**
     * 智能体是否正在说话
     */
    isAgentSpeaking: {
      type: Boolean,
      default: false,
    },
    /**
     * 智能体是否正在思考
     */
    isAgentThinking: {
      type: Boolean,
      default: false,
    },
    /**
     * 头像图片 URL
     */
    avatarImage: {
      type: String,
      default: '/xingxiaonge6c.gif',
    },
  },
});
</script>

<style scoped>
.avatar-panel {
  width: 50%;
  height: 100%;
  min-height: 460px;
  display: flex;
  justify-content: center;
  align-items: center;
  background: linear-gradient(135deg, #ffffff 0%, #f0f5ff 100%);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  transition: all 0.3s ease;
  position: relative;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: none;
  border: none;
}

.avatar-panel::before {
  content: '';
  position: absolute;
  top: -50%;
  left: -50%;
  width: 200%;
  height: 200%;
  background: radial-gradient(
    circle,
    rgba(0, 212, 255, 0.08) 0%,
    rgba(139, 92, 246, 0.06) 30%,
    transparent 70%
  );
  pointer-events: none;
}

.avatar-panel.agent-thinking::before {
  display: none;
}

.avatar-panel.full-width {
  width: 100%;
  height: 100%;
  min-height: 460px;
}

.avatar-container {
  width: 100%;
  height: 100%;
  min-height: 400px;
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  padding: 20px;
  background: #fafcfc;
  border-radius: 0;
  margin: 0;
  box-shadow: none;
  position: relative;
  overflow: hidden;
}

.avatar-container::before {
  display: none;
}

@keyframes borderRotate {
  0% {
    opacity: 0.8;
  }
  50% {
    opacity: 1;
  }
  100% {
    opacity: 0.8;
  }
}

.avatar-gif {
  width: 100%;
  height: 100%;
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  border-radius: 0;
  filter: none;
  transition: all 0.3s ease;
  position: relative;
  z-index: 1;
}

.avatar-gif:hover {
  filter: none;
  transform: scale(1.02);
}

.avatar-panel.agent-speaking {
  box-shadow:
    0 0 50px rgba(0, 212, 255, 0.3),
    0 0 100px rgba(139, 92, 246, 0.2),
    0 8px 32px rgba(0, 100, 200, 0.2);
  animation: pulse 1.5s ease-in-out infinite alternate;
}

.avatar-panel.agent-speaking .avatar-container {
  box-shadow: none;
}

.thinking-orbit {
  position: absolute;
  inset: clamp(12px, 3vw, 36px);
  display: flex;
  align-items: flex-start;
  justify-content: center;
  pointer-events: none;
  z-index: 2;
}

.thinking-orbit span {
  width: clamp(8px, 1.6vmin, 14px);
  height: clamp(8px, 1.6vmin, 14px);
  margin: clamp(2px, 0.5vmin, 6px) clamp(4px, 0.8vmin, 8px) 0;
  border-radius: 50%;
  background: rgba(0, 212, 255, 0.86);
  box-shadow: 0 0 14px rgba(0, 212, 255, 0.5);
  animation: thinkingDot 1.35s ease-in-out infinite;
}

.thinking-orbit span:nth-child(2) {
  animation-delay: 0.18s;
  background: rgba(139, 92, 246, 0.86);
  box-shadow: 0 0 14px rgba(139, 92, 246, 0.45);
}

.thinking-orbit span:nth-child(3) {
  animation-delay: 0.36s;
}

.avatar-panel.agent-thinking:not(.agent-speaking) {
  box-shadow:
    0 0 42px rgba(0, 212, 255, 0.18),
    0 0 88px rgba(139, 92, 246, 0.12);
}

@keyframes thinkingDot {
  0%,
  80%,
  100% {
    transform: translateY(-4px) scale(0.78);
    opacity: 0.45;
  }
  40% {
    transform: translateY(-14px) scale(1);
    opacity: 1;
  }
}

@keyframes pulse {
  0% {
    box-shadow:
      0 0 50px rgba(0, 212, 255, 0.3),
      0 0 100px rgba(139, 92, 246, 0.2),
      0 8px 32px rgba(0, 100, 200, 0.2);
  }
  100% {
    box-shadow:
      0 0 70px rgba(0, 212, 255, 0.45),
      0 0 140px rgba(139, 92, 246, 0.3),
      0 8px 32px rgba(0, 100, 200, 0.2);
  }
}
</style>
